import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { US_STOREFRONT_CURRENCY_ID } from '@/lib/checkout/regionalStoreDefaults'
import { createResilientStorage } from './resilientStorage'

interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  exp: number
}

type ModalType =
  | 'none'
  | 'options'
  | 'success'
  | 'alreadyPurchased'
  | 'error'
  | 'alreadyInCart'
  | 'outOfStock'
  | 'emailNotification'

interface ModalData {
  productId?: number
  productName?: string
  selectedOptionId?: number
  optionId?: number
  errorMessage?: string
  options?: {
    id: number
    description: string
    price: number
    salePrice?: number | null
    currencySymbol: string
    inStock: boolean
  }[]
}

interface Store {
  /** Cart token (non-guessable string). Use for all cart operations. */
  shoppingCartId: string | null
  setShoppingCartId: (id: string | null) => void
  user: User | null
  currency: number | null
  selectedOptionId: number | null
  country: string
  ecommBrandId: number | null
  countryFreeShippingStatus: boolean | null
  prepayImportDuties: boolean
  setPrepayImportDuties: (prepay: boolean) => void
  setUser: (user: User | null) => void
  setCurrency: (currency: number | null) => void
  setSelectedOptionId: (optionId: number | null) => void
  logout: () => void
  setCountry: (country: string) => void
  setCountryFreeShippingStatus: (status: boolean | null) => void
  setEcommBrandId: (ecommBrandId: number | null) => void
  currentModal: ModalType
  modalData: ModalData
  setModal: (modalType: ModalType, data?: ModalData) => void
  hydrated: boolean
  setHydrated: () => void
  /** Set by AuthProvider when initial verify has run (success or failure). Not persisted. */
  authResolved: boolean
  setAuthResolved: (value: boolean) => void
}

export const useStore = create<Store>()(
  persist(
    set => ({
      shoppingCartId: null,
      setShoppingCartId: (id: string | null) => set({ shoppingCartId: id }),
      user: null,
      currency: US_STOREFRONT_CURRENCY_ID,
      selectedOptionId: null,
      country: 'US',
      countryFreeShippingStatus: null,
      ecommBrandId: null,
      prepayImportDuties: false,
      setPrepayImportDuties: (prepay: boolean) =>
        set({ prepayImportDuties: prepay }),
      setUser: user => {
        set({ user })
      },
      setCurrency: currency => set({ currency }),
      setSelectedOptionId: optionId => set({ selectedOptionId: optionId }),
      logout: () => set({ user: null }),
      setCountry: country => set({ country }),
      setCountryFreeShippingStatus: (status: boolean | null) =>
        set({ countryFreeShippingStatus: status }),
      setEcommBrandId: ecommBrandId => set({ ecommBrandId }),
      currentModal: 'none',
      modalData: {},
      setModal: (modalType: ModalType, data: ModalData = {}) =>
        set({ currentModal: modalType, modalData: data }),
      hydrated: false,
      setHydrated: () => set({ hydrated: true }),
      authResolved: false,
      setAuthResolved: (value: boolean) => set({ authResolved: value }),
    }),
    {
      name: 'ViEcomm-store',
      storage: createJSONStorage(() => createResilientStorage()),
      skipHydration: typeof window === 'undefined',
      partialize: state => ({
        shoppingCartId: state.shoppingCartId,
        user: state.user,
        currency: state.currency,
        country: state.country,
        countryFreeShippingStatus: state.countryFreeShippingStatus,
        ecommBrandId: state.ecommBrandId,
        prepayImportDuties: state.prepayImportDuties,
      }),
      onRehydrateStorage: () => state => {
        if (state) {
          state.setHydrated()
        }
      },
    }
  )
)
